import React, { useState, useMemo, useRef } from 'react';
import { Pagination, Spin, DatePicker, Select, Button } from 'antd';
import 'antd/dist/antd.css';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import dayjs from 'dayjs';

/** ---------------- Helper functions ---------------- */
const ensureValidDate = (date, fallbackAddDays = 1) => {
  try {
    if (!date) {
      const d = new Date();
      d.setDate(d.getDate() + fallbackAddDays);
      return d;
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      const dd = new Date();
      dd.setDate(dd.getDate() + fallbackAddDays);
      return dd;
    }
    return d;
  } catch {
    const d = new Date();
    d.setDate(d.getDate() + fallbackAddDays);
    return d;
  }
};

const calculateProgress = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = ensureValidDate(startDate);
  const end = ensureValidDate(endDate);
  const now = new Date();
  const adjustedEnd = end > start ? end : new Date(start.getTime() + 86400000); // +1 day
  const total = adjustedEnd.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  if (total <= 0) return 0;
  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
};

/** ---------------- Period presets ---------------- */
const PERIOD_OPTIONS = [
  { label: 'Manual selection', value: 'manual' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Next 12 Months', value: 'next12m' },
  { label: '2019', value: '2019' },
  { label: '2020', value: '2020' },
  { label: '2021', value: '2021' },
  { label: '2022', value: '2022' },
  { label: '2023', value: '2023' },
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: 'Up to today', value: 'uptoToday' },
  { label: 'From today', value: 'fromToday' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 14 days', value: 'last14' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Next 7 days', value: 'next7' },
  { label: 'Next 14 days', value: 'next14' },
  { label: 'Next 30 days', value: 'next30' },
  { label: 'Up to next 7 days', value: 'uptoNext7' },
  { label: 'Up to next 14 days', value: 'uptoNext14' },
  { label: 'Up to next 30 days', value: 'uptoNext30' },
  { label: 'Last 14 and next 30 days', value: 'last14next30' },
  { label: 'Last 2 weeks and next 4 weeks', value: 'last2wNext4w' },
  { label: 'Last 4 weeks and next 8 weeks', value: 'last4wNext8w' },
  { label: 'Last 1 and next 3 months', value: 'last1mNext3m' },
  { label: 'Last 3 and next 9 months', value: 'last3mNext9m' },
  { label: 'Last 6 and next 12 months', value: 'last6mNext12m' },
  { label: 'This year and next 2 years', value: 'thisYearNext2' },
  { label: 'Since beginning of month', value: 'sinceMonthStart' },
  { label: 'Since beginning of this year', value: 'sinceYearStart' },
];

const computeRangeForPeriod = (value) => {
  const today = dayjs().startOf('day');
  switch (value) {
    case 'thisMonth': return [today.startOf('month'), today.endOf('month')];
    case 'thisYear': return [today.startOf('year'), today.endOf('year')];
    case 'next12m': return [today, today.add(12, 'month')];
    case '2019': return [dayjs('2019-01-01'), dayjs('2019-12-31')];
    case '2020': return [dayjs('2020-01-01'), dayjs('2020-12-31')];
    case '2021': return [dayjs('2021-01-01'), dayjs('2021-12-31')];
    case '2022': return [dayjs('2022-01-01'), dayjs('2022-12-31')];
    case '2023': return [dayjs('2023-01-01'), dayjs('2023-12-31')];
    case '2024': return [dayjs('2024-01-01'), dayjs('2024-12-31')];
    case '2025': return [dayjs('2025-01-01'), dayjs('2025-12-31')];
    case 'uptoToday': return [dayjs('1970-01-01'), today.endOf('day')];
    case 'fromToday': return [today, today.add(10, 'year')];
    case 'last7': return [today.subtract(7, 'day'), today.endOf('day')];
    case 'last14': return [today.subtract(14, 'day'), today.endOf('day')];
    case 'last30': return [today.subtract(30, 'day'), today.endOf('day')];
    case 'next7': return [today, today.add(7, 'day')];
    case 'next14': return [today, today.add(14, 'day')];
    case 'next30': return [today, today.add(30, 'day')];
    case 'uptoNext7': return [dayjs('1970-01-01'), today.add(7, 'day')];
    case 'uptoNext14': return [dayjs('1970-01-01'), today.add(14, 'day')];
    case 'uptoNext30': return [dayjs('1970-01-01'), today.add(30, 'day')];
    case 'last14next30': return [today.subtract(14, 'day'), today.add(30, 'day')];
    case 'last2wNext4w': return [today.subtract(14, 'day'), today.add(28, 'day')];
    case 'last4wNext8w': return [today.subtract(28, 'day'), today.add(56, 'day')];
    case 'last1mNext3m': return [today.subtract(1, 'month'), today.add(3, 'month')];
    case 'last3mNext9m': return [today.subtract(3, 'month'), today.add(9, 'month')];
    case 'last6mNext12m': return [today.subtract(6, 'month'), today.add(12, 'month')];
    case 'thisYearNext2': return [today.startOf('year'), today.add(2, 'year').endOf('year')];
    case 'sinceMonthStart': return [today.startOf('month'), today];
    case 'sinceYearStart': return [today.startOf('year'), today];
    default: return [null, null];
  }
};

/** ---------------- Tooltip for Gantt ---------------- */
const TooltipContent = ({ task }) => (
  <div style={{ padding: 8, color: '#000' }}>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>{task.name || 'Timeline'}</div>
    <div>Start: {task.start?.toLocaleDateString?.() ?? ''}</div>
    <div>End:&nbsp;&nbsp;{task.end?.toLocaleDateString?.() ?? ''}</div>
  </div>
);

/** ---------------- Custom Task List Component ---------------- */
const CustomTaskList = ({ tasks, onExpanderClick, onTaskSelect, rowHeight = 40 }) => {
  const [hoveredTask, setHoveredTask] = useState(null);

  const handleMouseEnter = (taskId) => setHoveredTask(taskId);
  const handleMouseLeave = () => setHoveredTask(null);

  return (
    <div style={{ width: 250, overflowX: 'auto', borderRight: '1px solid #d9d9d9', flexShrink: 0 }}>
      <div style={{ height: 50, padding: '0 12px', backgroundColor: '#f0f0f0', fontWeight: 'bold', color: '#000', borderBottom: '1px solid #d9d9d9', display: 'flex', alignItems: 'center' }}>
        Task Name
      </div>
      <div style={{ backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', fontSize: '12px', overflowX: 'auto', overflowY: 'hidden' }}>
        {tasks.map(task => (
          <div
            key={task.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: rowHeight,
              padding: '0 12px',
              borderBottom: '1px solid #e8e8e8',
              color: '#000',
              cursor: 'pointer',
              backgroundColor: hoveredTask === task.id ? '#f5f5f5' : 'transparent',
            }}
            onMouseEnter={() => handleMouseEnter(task.id)}
            onMouseLeave={handleMouseLeave}
            onClick={() => onTaskSelect(task)}
          >
            {task.type === 'project' && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onExpanderClick(task);
                }}
                style={{ cursor: 'pointer', width: 20, textAlign: 'center', marginRight: 4, fontWeight: 'bold', color: '#000' }}
              >
                {task.hideChildren ? '+' : '−'}
              </div>
            )}
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                color: '#000',
              }}
              title={task.name} // Added tooltip here
            >
              {task.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** ---------------- Main Component ---------------- */
const GanttChart = React.memo(({ initiatives }) => {
  const [currentViewMode] = useState(ViewMode.Month);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [period, setPeriod] = useState('manual');
  const [[fromDate, toDate], setDateRange] = useState([null, null]);
  const [expandedProjects, setExpandedProjects] = useState(() => new Set());
  const rowHeight = 40;

  /** -------- Pagination slice -------- */
  const paginatedInitiatives = useMemo(() => {
    if (!initiatives || !Array.isArray(initiatives)) return [];
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return initiatives.slice(start, end);
  }, [initiatives, currentPage, pageSize]);

  /** -------- Build tasks (parents + children) -------- */
  const ganttTasks = useMemo(() => {
    setIsLoading(true);
    const tasks = [];

    if (!paginatedInitiatives || paginatedInitiatives.length === 0) {
      setIsLoading(false);
      return [];
    }

    paginatedInitiatives.forEach((initiative) => {
      const initiativeId = `initiative-${initiative.initiativeNumber}`;
      const initiativeStart = ensureValidDate(initiative.created, 0);
      const l4Stage = initiative.timeline?.find(item => item.stage === 'L4');
      const initiativeEnd = ensureValidDate(l4Stage?.date?.plan, 30);
      const adjustedInitiativeEnd =
        initiativeEnd > initiativeStart ? initiativeEnd : new Date(initiativeStart.getTime() + 2592000000);
      const initiativeProgress = calculateProgress(initiativeStart, adjustedInitiativeEnd);

      tasks.push({
        id: initiativeId,
        name: initiative.initiativeName || `Initiative ${initiative.initiativeNumber}`,
        start: initiativeStart,
        end: adjustedInitiativeEnd,
        progress: initiativeProgress,
        type: 'project',
        hideChildren: !expandedProjects.has(initiativeId),
        styles: {
          backgroundColor: '#4e73df',
          backgroundSelectedColor: '#4e73df',
          progressColor: '#2e59d9',
          progressSelectedColor: '#2e59d9',
        },
      });

      if (Array.isArray(initiative.milestones) && expandedProjects.has(initiativeId)) {
        initiative.milestones.forEach((milestone) => {
          const milestoneStart = ensureValidDate(milestone.startDate, 1);
          const milestoneEnd = ensureValidDate(milestone.endDate, 7);
          const adjustedMilestoneEnd =
            milestoneEnd > milestoneStart ? milestoneEnd : new Date(milestoneStart.getTime() + 604800000);
          const milestoneProgress = calculateProgress(milestoneStart, adjustedMilestoneEnd);

          tasks.push({
            id: `milestone-${milestone.milestoneNumber}`,
            name: milestone.name || `Milestone #${milestone.milestoneNumber}`,
            start: milestoneStart,
            end: adjustedMilestoneEnd,
            progress: milestoneProgress,
            type: 'task',
            project: initiativeId,
            dependencies: [initiativeId],
            styles: {
              backgroundColor: '#8a2be2',
              backgroundSelectedColor: '#8a2be2',
              progressColor: '#6a1b9a',
              progressSelectedColor: '#6a1b9a',
            },
          });
        });
      }
    });

    setIsLoading(false);
    return tasks;
  }, [paginatedInitiatives, expandedProjects]);

  /** -------- Selection filter -------- */
  const selectionFilteredTasks = useMemo(() => {
    if (!selectedKey) return ganttTasks;
    if (selectedKey.startsWith('initiative-')) {
      return ganttTasks.filter(
        (task) => task.id === selectedKey || task.project === selectedKey
      );
    }
    if (selectedKey.startsWith('milestone-')) {
      const milestone = ganttTasks.find(t => t.id === selectedKey);
      if (milestone) {
        return ganttTasks.filter(t => t.id === selectedKey || t.id === milestone.project);
      }
      return [milestone];
    }
    return ganttTasks;
  }, [selectedKey, ganttTasks]);

  /** -------- Date filtering -------- */
  const dateFilteredTasks = useMemo(() => {
    if (!fromDate || !toDate) return selectionFilteredTasks;
    const start = fromDate.startOf('day').toDate();
    const end = toDate.endOf('day').toDate();
    const filtered = selectionFilteredTasks.filter((t) => t.start <= end && t.end >= start);
    return filtered.map(task => ({
      ...task,
      start: dayjs(task.start).toDate(),
      end: dayjs(task.end).toDate(),
    }));
  }, [selectionFilteredTasks, fromDate, toDate]);

  /** -------- Gantt config -------- */
  const ganttConfig = useMemo(() => ({
    headerHeight: 50,
    columnWidth: 80,
    barHeight: 20,
    barCornerRadius: 3,
    arrowColor: '#ff0000',
    arrowIndent: 20,
    rowHeight: rowHeight,
    fontSize: '12px',
    barTextColor: '#000',
    showTaskList: false, // The key change to hide the default task list
  }), [rowHeight]);

  /** -------- Period select change -------- */
  const onChangePeriod = (value) => {
    setPeriod(value);
    const [from, to] = computeRangeForPeriod(value);
    setDateRange([from, to]);
  };

  /** -------- Expand/collapse -------- */
  const handleExpanderClick = (task) => {
    if (task?.type !== 'project') return;
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(task.id)) {
        next.delete(task.id);
      } else {
        next.add(task.id);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" tip="Loading Gantt Chart..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <style>{`
        .gantt-container {
          display: flex;
          flex-direction: row;
        }
        .gantt-chart-wrapper {
          overflow-x: auto;
          flex: 1;
        }
        /* Hide text in the main Gantt chart area */
        .gantt-container .bar-label,
        .gantt-container .bar-label-inner {
            display: none !important;
        }
      `}</style>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Period:</span>
          <Select
            value={period}
            onChange={onChangePeriod}
            options={PERIOD_OPTIONS}
            style={{ width: 230 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>From:</span>
          <DatePicker
            value={fromDate}
            onChange={(v) => {
              setDateRange([v, toDate]);
              setPeriod('manual');
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600 }}>To:</span>
          <DatePicker
            value={toDate}
            onChange={(v) => {
              setDateRange([fromDate, v]);
              setPeriod('manual');
            }}
          />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button onClick={() => setSelectedKey(null)} size="small">
            Clear selection
          </Button>
        </div>
      </div>

      <div style={{ border: '1px solid #d9d9d9', padding: 10, borderRadius: 5 }}>
        <h2 style={{ marginTop: 0, color: '#000' }}>Gantt Chart</h2>
        {dateFilteredTasks.length > 0 ? (
          <div style={{ display: 'flex' }}>
            <CustomTaskList
              tasks={dateFilteredTasks}
              onExpanderClick={handleExpanderClick}
              onTaskSelect={(task) => setSelectedKey(task.id)}
              rowHeight={rowHeight}
            />
            
            <div style={{ flex: 1, overflowX: 'auto' }}>
              <Gantt
                tasks={dateFilteredTasks}
                {...ganttConfig}
                viewMode={currentViewMode}
                locale="en"
                TooltipContent={TooltipContent}
                onSelect={(task) => setSelectedKey(task.id)}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
              border: '1px dashed #d9d9d9',
              borderRadius: 5,
              color: '#000',
            }}
          >
            <p>No tasks to display.</p>
          </div>
        )}
      </div>

      {initiatives && initiatives.length > pageSize && (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={initiatives.length}
            onChange={(page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize) setPageSize(newPageSize);
            }}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={['10', '20', '50', '100']}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          />
        </div>
      )}
    </div>
  );
});

export default GanttChart;